import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

// (Msg 753) Lightweight EN/ES language toggle. No i18n library — just a
// React context + a tiny strings dict + a field-swap helper that reads
// "X (ES)" columns from the JSON when Spanish is active. Per-card fallback
// to English when the Spanish field is empty.

const STORAGE_KEY = 'wsp_lang';
const SUPPORTED = ['en', 'es'];

export const UI_STRINGS = {
  en: {
    // City picker
    cityPickerLabel: 'Choose Your City',
    allCanada: 'ALL CANADA',
    allMexico: 'ALL MEXICO',
    allUsa: 'ALL USA',
    allCities: 'ALL CITIES',
    // Section headings
    curatedPicks: 'CURATED PICKS',
    fullGuide: 'Full Guide',
    filterBy: 'Filter by',
    readMore: 'Read More',
    viewDetails: 'View Details',
    poweredBy: 'Your local fan guide',
    noResults: 'No results found',
    thingsToDo: 'Things To Do',
    matchSchedule: 'Match Schedule',
    gameHighlights: 'Game Highlights',
    blog: 'Blog',
    home: 'Home',
    eventsNearMe: 'Events Near Me',
    advertise: 'Advertise',
    aboutUs: 'About Us',
    listYourEvent: 'List Event Free',
    share: 'Share',
    // Things to do labels
    saveToTrip: 'Save to Trip',
    addToTrip: 'Add to Trip',
    sponsoredPartner: 'Sponsored Partner',
    summer2026Edition: 'Summer 2026',
    edition: 'Edition',
    allCitiesEdition: 'All Cities Edition',
    // Vibes
    eat: 'Eat', shop: 'Shop', drink: 'Drink', watch: 'Watch',
    party: 'Party', explore: 'Explore', chill: 'Chill', all: 'All',
    whatsYourVibe: "What's your vibe in",
    // Footer / static
    quickLinks: 'Quick Links',
    follow: 'Follow Us',
    guidesAndBlog: 'Guides & Blog',
    eventListings: 'Event Listings',
    allBlogPosts: 'All Blog Posts',
    // Match schedule
    stadiumLocal: 'Stadium Local',
    yourTz: 'Your TZ',
    group: 'Group',
    match: 'Match',
    venue: 'Venue',
    score: 'Score',
    calendar: 'Calendar',
    timesVerified: 'Times Verified',
    // Game highlights
    autoUpdated: 'Auto-Updated',
    readRecap: 'Read Recap',
    backToBlog: 'Back to Blog',
    backToGameHighlights: 'Game Highlights',
    moreGameHighlights: 'More Game Highlights',
    final: 'Final',
    goals: 'Goals',
  },
  es: {
    cityPickerLabel: 'Elige Tu Ciudad',
    allCanada: 'TODO CANADÁ',
    allMexico: 'TODO MÉXICO',
    allUsa: 'TODO EE.UU.',
    allCities: 'TODAS LAS CIUDADES',
    curatedPicks: 'SELECCIONES CURADAS',
    fullGuide: 'Guía Completa',
    filterBy: 'Filtrar por',
    readMore: 'Leer Más',
    viewDetails: 'Ver Detalles',
    poweredBy: 'Tu guía local de fans',
    noResults: 'No se encontraron resultados',
    thingsToDo: 'Qué Hacer',
    matchSchedule: 'Calendario de Partidos',
    gameHighlights: 'Resúmenes del Partido',
    blog: 'Blog',
    home: 'Inicio',
    eventsNearMe: 'Eventos Cerca de Mí',
    advertise: 'Anuncia',
    aboutUs: 'Acerca de',
    listYourEvent: 'Publica Gratis',
    share: 'Compartir',
    saveToTrip: 'Guardar al Viaje',
    addToTrip: 'Añadir al Viaje',
    sponsoredPartner: 'Patrocinador',
    summer2026Edition: 'Verano 2026',
    edition: 'Edición',
    allCitiesEdition: 'Edición Todas las Ciudades',
    eat: 'Comer', shop: 'Comprar', drink: 'Beber', watch: 'Ver',
    party: 'Fiesta', explore: 'Explorar', chill: 'Relax', all: 'Todo',
    whatsYourVibe: '¿Cuál es tu vibra en',
    quickLinks: 'Enlaces Rápidos',
    follow: 'Síguenos',
    guidesAndBlog: 'Guías y Blog',
    eventListings: 'Listados de Eventos',
    allBlogPosts: 'Todas las Publicaciones',
    stadiumLocal: 'Hora Local del Estadio',
    yourTz: 'Tu Zona Horaria',
    group: 'Grupo',
    match: 'Partido',
    venue: 'Estadio',
    score: 'Marcador',
    calendar: 'Calendario',
    timesVerified: 'Horarios Verificados',
    autoUpdated: 'Actualizado Automáticamente',
    readRecap: 'Leer Resumen',
    backToBlog: 'Volver al Blog',
    backToGameHighlights: 'Resúmenes',
    moreGameHighlights: 'Más Resúmenes',
    final: 'Final',
    goals: 'Goles',
  },
};

const detectInitialLang = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED.includes(stored)) return stored;
    const nav = (navigator.language || navigator.userLanguage || 'en').toLowerCase();
    return nav.startsWith('es') ? 'es' : 'en';
  } catch {
    return 'en';
  }
};

const LanguageContext = createContext({
  lang: 'en',
  setLang: () => {},
  toggle: () => {},
  t: (k) => k,
  getLocalized: (item, field) => item?.[field],
});

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState('en');

  // Hydrate after mount (avoids SSR mismatch — though CRA is CSR-only,
  // still keep localStorage read inside effect for safety).
  useEffect(() => {
    setLangState(detectInitialLang());
  }, []);

  const setLang = useCallback((next) => {
    if (!SUPPORTED.includes(next)) return;
    setLangState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch {}
  }, []);

  const toggle = useCallback(() => {
    setLang(lang === 'en' ? 'es' : 'en');
  }, [lang, setLang]);

  // Translate a UI key. Falls back to English then to the key itself.
  const t = useCallback((key) => {
    return UI_STRINGS[lang]?.[key] ?? UI_STRINGS.en[key] ?? key;
  }, [lang]);

  // Read a localized field from a JSON entry. Maps base field → "Field (ES)"
  // when Spanish is active; falls back to English if the ES field is empty.
  const getLocalized = useCallback((item, field) => {
    if (!item) return '';
    if (lang === 'es') {
      const esKey = `${field} (ES)`;
      const esVal = item[esKey];
      if (esVal && String(esVal).trim()) return esVal;
    }
    return item[field] ?? '';
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, toggle, t, getLocalized }), [lang, setLang, toggle, t, getLocalized]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);
